import { useEffect, useState, useRef } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../../components/ConfirmModal';
import { getPushPreferences, savePushPreferences } from '../../utils/pushPreferences.js';
import { uploadTrainingImage } from '../../utils/trainingImages.js';
import { errorText } from '../../utils/errorMessages.js';

const CALENDAR_FEED_BASE = 'https://rvkigbljdpkiqzttedwu.supabase.co/functions/v1/calendar-feed';

// Alle instellingen op deze pagina lopen via één upsert op `settings`. Die kan
// door RLS stil 0 rijen raken (settings_update staat alleen superadmin/
// admin_coach toe) zonder dat PostgREST een fout teruggeeft — zie BRIEFING
// hoofdstuk 8. Deze helper legt de rij-check op één plek vast: elke
// meegegeven sleutel moet terugkomen, anders een harde fout die de
// bestaande catch-blokken al opvangen en tonen.
async function upsertSettings(rows) {
  const { data, error } = await supabase
    .from('settings')
    .upsert(rows, { onConflict: 'key' })
    .select('key');
  if (error) throw error;
  if ((data?.length ?? 0) !== rows.length) throw new Error(t('common.wijzigingNietOpgeslagen'));
  return data;
}

// Moet gelijk blijven aan de databaseconstraint chk_bio_length
// (20260803130000_lengtebeperking_bio_bring_items.sql).
const BIO_MAX_LENGTH = 1000;

const COMPANY_KEYS = [
  'company_name',
  'company_address',
  'company_postcode',
  'company_city',
  'company_kvk',
  'company_btw',
  'company_iban',
  'company_email',
  'company_phone',
  'company_payment_term_days',
  'invoice_footer',
  'company_logo_url',
  'email_from',
  'email_invoices',
  'email_agreements',
  'email_notifications',
  'gym_url',
  'app_store_url',
  'play_store_url',
];

const EMAIL_AUTO_KEYS = [
  'email_auto_reminder',
  'email_auto_final_notice',
  'email_auto_recurring_invoices',
  'push_reminder_enabled',
  'push_reminder_text',
];

const PROSPECT_REMINDER_KEYS = [
  'prospect_reminder_enabled',
  'prospect_reminder_interval_days',
  'prospect_reminder_max',
];

const AGREEMENT_REMINDER_KEYS = [
  'agreement_reminder_enabled',
  'agreement_reminder_interval_days',
  'agreement_reminder_max',
];

const MEMBERSHIP_REMINDER_KEYS = [
  'reminder_days_before_prolongation',
  'pending_confirmation_expiry_hours',
];

const PAYMENT_METHOD_KEYS = [
  'payment_method_ideal_enabled',
  'payment_method_sepa_enabled',
  'mollie_incassant_id',
];

// Weekmodel credits (2026-08-08). credit_default_expiry_interval wordt als
// PostgreSQL-interval opgeslagen ('3 mons' / '6 weeks'). Invoer altijd als
// getal + eenheid-dropdown, nooit als vrije tekst — een niet-parseerbare
// interval zou de toekenningscron laten terugvallen op de harde default.
const CREDIT_SETTING_KEYS = [
  'credit_default_expiry_interval',
  'credit_session_warning_days',
];

// Fitness tracking deel 7: herinnering aan de coach bij niet-gelogde
// PT-sessies. Instance-breed (timing/frequentie); de coach zelf bepaalt
// via push_preferences.client_activity of hij dit type wil ontvangen.
const UNLOGGED_REMINDER_KEYS = [
  'unlogged_session_reminder_enabled',
  'unlogged_session_reminder_delay_hours',
  'unlogged_session_reminder_frequency',
];

// Onderdeel 14 (2026-08-17): systeembrede hoofdschakelaar voor zelfstandig
// loggen door cliënten. Lezen mag iedereen (settings_read), maar wijzigen
// loopt bewust NIET via de generieke settings-upsert: dat zou ook
// admin_coach toegang geven. Het gaat hier via de RPC
// `set_client_self_logging_enabled`, die uitsluitend superadmin toestaat.
const SELF_LOGGING_KEY = 'client_self_logging_enabled';

function parseExpiryInterval(raw) {
  const tekst = (raw || '').trim().toLowerCase();
  const m = tekst.match(/^(\d+)\s*(mon|mons|month|months|week|weeks)$/);
  if (!m) return { aantal: '3', eenheid: 'mons' };
  return { aantal: m[1], eenheid: m[2].startsWith('week') ? 'weeks' : 'mons' };
}

const ORGANISATION_SECTIONS = [
  { id: 'bedrijf', labelKey: 'beheer.bedrijfsgegevens' },
  { id: 'facturatie', labelKey: 'beheer.facturatieBetalingen' },
  { id: 'automatisering', labelKey: 'beheer.automatisering' },
  { id: 'credits', labelKey: 'beheer.creditsTrainingen' },
  { id: 'branding', labelKey: 'beheer.portaalBranding' },
  { id: 'juridisch', labelKey: 'beheer.juridisch' },
];

export default function InstellingenPage({ view = 'profile' }) {
  const { role, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const isAdmin = ['superadmin', 'admin_coach'].includes(role);
  const { section = 'bedrijf' } = useParams();
  const activeOrganisationSection = ORGANISATION_SECTIONS.some(item => item.id === section) ? section : 'bedrijf';

  // ── Push-instellingen state (C4, zichtbaar voor alle coach-rollen) ──
  const [pushPrefs, setPushPrefs] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    getPushPreferences(supabase, user.id).then(setPushPrefs).catch(() => {});
  }, [user?.id]);

  async function handleTogglePushCat(key, checked) {
    const previous = pushPrefs;
    const next = { ...pushPrefs, [key]: checked };
    setPushPrefs(next);
    try {
      await savePushPreferences(supabase, user.id, next);
    } catch (e) {
      setPushPrefs(previous);
      showError(t('pushCat.foutOpslaan') + e.message);
    }
  }

  // ── Opzeg-mail-instelling (Onderdeel 1, zichtbaar voor alle coach-rollen) ──
  const [receivesCancellationEmails, setReceivesCancellationEmails] = useState(true);
  const [cancelEmailLoading, setCancelEmailLoading] = useState(true);
  const [cancelEmailSaving, setCancelEmailSaving] = useState(false);

  // ── Korte coach-introductie (2026-08-01, zichtbaar voor alle coach-rollen) ──
  // Zichtbaar voor cliënten in het trainingsdetailscherm van de app, via de
  // view coach_basic_profiles. Meegeladen in dezelfde profielquery hieronder.
  const [bio, setBio] = useState('');
  const [bioSaving, setBioSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from('profiles').select('receives_cancellation_emails, bio, avatar_url').eq('id', user.id).maybeSingle()
      .then(({ data, error: err }) => {
        if (err) showError(t('opzegMailInstelling.foutLaden') + err.message);
        else {
          setReceivesCancellationEmails(data?.receives_cancellation_emails ?? true);
          setBio(data?.bio ?? '');
          setAvatarUrl(data?.avatar_url ?? '');
        }
      })
      .finally(() => setCancelEmailLoading(false));
  }, [user?.id]);

  async function handleToggleCancellationEmails(checked) {
    const previous = receivesCancellationEmails;
    setReceivesCancellationEmails(checked);
    setCancelEmailSaving(true);
    try {
      const { data: updRow, error: err } = await supabase
        .from('profiles')
        .update({ receives_cancellation_emails: checked })
        .eq('id', user.id)
        .select('id')
        .maybeSingle();
      if (err) throw err;
      if (!updRow) {
        setReceivesCancellationEmails(previous);
        showError(t('common.wijzigingNietOpgeslagen'));
        return;
      }
    } catch (e) {
      setReceivesCancellationEmails(previous);
      showError(t('opzegMailInstelling.foutOpslaan') + e.message);
    } finally {
      setCancelEmailSaving(false);
    }
  }

  async function handleSaveBio() {
    setBioSaving(true);
    try {
      const { data: updRow, error: err } = await supabase
        .from('profiles')
        .update({ bio: bio.trim() || null })
        .eq('id', user.id)
        .select('id')
        .maybeSingle();
      if (err) throw err;
      if (!updRow) { showError(t('common.wijzigingNietOpgeslagen')); return; }
      showSuccess(t('coachBio.succes'));
    } catch (e) {
      showError(t('coachBio.fout') + e.message);
    } finally {
      setBioSaving(false);
    }
  }

  async function handleAvatarUpload(file) {
    if (!file) return;
    setAvatarUploading(true);
    try {
      const url = await uploadTrainingImage(supabase, 'coaches', user.id, file);
      const { data: updRow, error: err } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', user.id)
        .select('id')
        .maybeSingle();
      if (err) throw err;
      if (!updRow) { showError(t('common.wijzigingNietOpgeslagen')); return; }
      setAvatarUrl(url);
      showSuccess(t('coachBio.fotoSucces'));
    } catch {
      showError(t('coachBio.fotoFout'));
    } finally {
      setAvatarUploading(false);
    }
  }

  // ── Wachtwoord wijzigen (zichtbaar voor alle coach-rollen) ──
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  async function handleChangePassword(e) {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      showError(t('wachtwoordWijzigen.foutTeKort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      showError(t('wachtwoordWijzigen.foutMismatch'));
      return;
    }
    setChangingPassword(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: newPassword });
      if (err) throw err;
      setNewPassword('');
      setConfirmPassword('');
      showSuccess(t('wachtwoordWijzigen.succes'));
    } catch (err) {
      showError(t('wachtwoordWijzigen.fout') + err.message);
    } finally {
      setChangingPassword(false);
    }
  }

  // ── Agenda-feed state (zichtbaar voor alle coach-rollen) ──
  const [feedToken, setFeedToken] = useState(null);
  const [feedLoading, setFeedLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [feedError, setFeedError] = useState('');
  const feedUrl = feedToken ? `${CALENDAR_FEED_BASE}?token=${feedToken}` : null;

  useEffect(() => {
    supabase.rpc('get_calendar_feed_token')
      .then(({ data, error: err }) => {
        if (err) setFeedError(t('coachInstellingen.foutLaden') + err.message);
        else setFeedToken(data || null);
      })
      .finally(() => setFeedLoading(false));
  }, []);

  async function doGenerate() {
    setGenerating(true);
    setFeedError('');
    try {
      const { data, error: err } = await supabase.rpc('generate_calendar_feed_token');
      if (err) throw err;
      setFeedToken(data);
    } catch (e) {
      setFeedError(t('coachInstellingen.foutGenereren') + e.message);
    } finally {
      setGenerating(false);
      setShowConfirm(false);
    }
  }

  async function handleCopy() {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Kopiëren kan mislukken buiten een secure context of zonder
      // klembordtoestemming. Zonder melding lijkt de knop simpelweg niets te
      // doen en plakt de coach een verouderde/lege URL in Google Agenda.
      console.error('Fout bij kopiëren agenda-URL:', e);
      setFeedError(t('coachInstellingen.foutKopieren'));
    }
  }

  // ── Privé-agenda ICS-koppeling state (zichtbaar voor alle coach-rollen) ──
  const [privCal, setPrivCal] = useState({ ics_url: '', share_details: false });
  const [privCalLoading, setPrivCalLoading] = useState(true);
  const [privCalSaving, setPrivCalSaving] = useState(false);
  const [privCalSyncing, setPrivCalSyncing] = useState(false);
  const [privCalStatus, setPrivCalStatus] = useState({ last_synced_at: null, last_sync_status: null, last_sync_message: null });
  const [privCalError, setPrivCalError] = useState('');

  async function loadPrivCal() {
    setPrivCalLoading(true);
    setPrivCalError('');
    try {
      const { data, error: err } = await supabase.rpc('get_my_external_calendar_settings');
      if (err) throw err;
      const row = Array.isArray(data) ? data[0] : data;
      if (row) {
        setPrivCal({
          ics_url: row.ics_url || '',
          share_details: !!row.share_details,
        });
        setPrivCalStatus({
          last_synced_at: row.last_synced_at || null,
          last_sync_status: row.last_sync_status || null,
          last_sync_message: row.last_sync_message || null,
        });
      }
    } catch (e) {
      setPrivCalError(t('privateAgendaKoppeling.foutLaden') + e.message);
    } finally {
      setPrivCalLoading(false);
    }
  }

  useEffect(() => { loadPrivCal(); }, []);

  async function handleSavePrivCal(e) {
    e.preventDefault();
    if (!privCal.ics_url.trim()) {
      showError(t('privateAgendaKoppeling.foutLegeUrl'));
      return;
    }
    setPrivCalSaving(true);
    try {
      const { error: err } = await supabase.rpc('upsert_my_external_calendar_settings', {
        p_ics_url: privCal.ics_url.trim(),
        p_share_details: privCal.share_details,
        // Gedeprecate kolom: externe conflicten zijn altijd een overrulebare
        // waarschuwing; harde blokkade bestaat niet meer (2026-07-13).
        p_block_on_conflict: false,
      });
      if (err) throw err;
      showSuccess(t('privateAgendaKoppeling.opgeslagen'));
      await loadPrivCal();
    } catch (e) {
      showError(t('privateAgendaKoppeling.foutOpslaan') + e.message);
    } finally {
      setPrivCalSaving(false);
    }
  }

  async function handleSyncNow() {
    setPrivCalSyncing(true);
    try {
      const { data, error: err } = await supabase.functions.invoke('sync-external-calendar', { body: {} });
      if (err) throw err;
      const result = Array.isArray(data?.results) ? data.results[0] : null;
      if (result && !result.success) {
        showError(t('privateAgendaKoppeling.foutSync') + result.message);
      } else {
        showSuccess(t('privateAgendaKoppeling.syncGelukt'));
      }
      await loadPrivCal();
    } catch (e) {
      showError(t('privateAgendaKoppeling.foutSync') + e.message);
    } finally {
      setPrivCalSyncing(false);
    }
  }

  // ── Admin bedrijfsgegevens state ──
  const [fields, setFields] = useState({
    company_name: '',
    company_address: '',
    company_postcode: '',
    company_city: '',
    company_kvk: '',
    company_btw: '',
    company_iban: '',
    company_email: '',
    company_phone: '',
    company_payment_term_days: '14',
    invoice_footer: '',
    company_logo_url: '',
    email_from: '',
    email_invoices: '',
    email_agreements: '',
    email_notifications: '',
    gym_url: '',
    app_store_url: '',
    play_store_url: '',
  });
  const [emailAuto, setEmailAuto] = useState({
    email_auto_reminder: 'true',
    email_auto_final_notice: 'false',
    email_auto_recurring_invoices: 'false',
    push_reminder_enabled: 'true',
    push_reminder_text: '',
  });
  const [autoRecurringInvoicesConfirm, setAutoRecurringInvoicesConfirm] = useState(false);
  const [prospectReminder, setProspectReminder] = useState({
    prospect_reminder_enabled: 'true',
    prospect_reminder_interval_days: '7',
    prospect_reminder_max: '3',
  });
  const [agreementReminder, setAgreementReminder] = useState({
    agreement_reminder_enabled: 'true',
    agreement_reminder_interval_days: '3',
    agreement_reminder_max: '2',
  });
  // Onderdeel 3 (2026-07-27, Midden-fix — Opus-review): laatst opgeslagen
  // enabled/max, om te bepalen of een save-poging verhoogt/inschakelt (mag
  // geblokkeerd worden bij incomplete teksten) of verlaagt/uitschakelt (mag
  // ALTIJD lukken, ook met incomplete teksten — anders is er geen manier meer
  // om de automatisering uit te zetten). Bijgewerkt bij laden én bij elke
  // geslaagde save.
  const prospectReminderSavedRef = useRef({ enabled: 'true', max: '3' });
  const agreementReminderSavedRef = useRef({ enabled: 'true', max: '2' });
  const [generalTerms, setGeneralTerms] = useState('');
  const [savingGeneralTerms, setSavingGeneralTerms] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingEmailAuto, setSavingEmailAuto] = useState(false);
  const [savingProspectReminder, setSavingProspectReminder] = useState(false);
  const [savingAgreementReminder, setSavingAgreementReminder] = useState(false);
  const [missingTextsModal, setMissingTextsModal] = useState(null); // 'agreement' | 'prospect' | null
  const [membReminderDays, setMembReminderDays] = useState({ 14: true, 7: true, 3: true });
  const [savingMembReminder, setSavingMembReminder] = useState(false);
  const [expiryHours, setExpiryHours] = useState('48');
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [maxBroadcasts, setMaxBroadcasts] = useState('10');
  const [savingBroadcasts, setSavingBroadcasts] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({
    payment_method_ideal_enabled: 'false',
    payment_method_sepa_enabled: 'false',
    mollie_incassant_id: '',
  });
  const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
  // Weekmodel credits (2026-08-08)
  const [creditExpiry, setCreditExpiry] = useState({ aantal: '3', eenheid: 'mons' });
  const [creditWarningDays, setCreditWarningDays] = useState('3');
  const [savingCredits, setSavingCredits] = useState(false);
  // Fitness tracking deel 7: niet-gelogde-PT-sessie-herinnering
  const [unloggedReminder, setUnloggedReminder] = useState({
    unlogged_session_reminder_enabled: 'false',
    unlogged_session_reminder_delay_hours: '3',
    unlogged_session_reminder_frequency: 'once',
  });
  const [savingUnloggedReminder, setSavingUnloggedReminder] = useState(false);
  // Onderdeel 14: systeembrede self-loggingschakelaar (superadmin-only).
  const [selfLoggingEnabled, setSelfLoggingEnabled] = useState(false);
  const [savingSelfLogging, setSavingSelfLogging] = useState(false);
  // F4-10 Stap 2: live telling actieve client_memberships per methode, voor de
  // waarschuwing bij uitschakelen. null = nog niet opgehaald.
  const [activeMembershipCounts, setActiveMembershipCounts] = useState({ ideal: null, sepa: null });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // ── Locatiebeheer state ──
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [locModal, setLocModal] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  const [locForm, setLocForm] = useState({
    name: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    image_url: '',
  });
  const [savingLoc, setSavingLoc] = useState(false);
  const [locationImageUploading, setLocationImageUploading] = useState(false);
  const [locToggleConfirm, setLocToggleConfirm] = useState(null); // loc object

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    async function load() {
      setLoading(true);
      try {
        const { data, error: err } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', [...COMPANY_KEYS, ...EMAIL_AUTO_KEYS, ...PROSPECT_REMINDER_KEYS, ...AGREEMENT_REMINDER_KEYS, ...MEMBERSHIP_REMINDER_KEYS, ...PAYMENT_METHOD_KEYS, ...CREDIT_SETTING_KEYS, ...UNLOGGED_REMINDER_KEYS, SELF_LOGGING_KEY, 'max_broadcasts_per_day', 'agreement_general_terms']);
        if (err) throw err;
        const map = {};
        (data || []).forEach(({ key, value }) => { map[key] = value ?? ''; });
        setFields(prev => ({ ...prev, ...Object.fromEntries(COMPANY_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
        setEmailAuto(prev => ({ ...prev, ...Object.fromEntries(EMAIL_AUTO_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
        setProspectReminder(prev => ({ ...prev, ...Object.fromEntries(PROSPECT_REMINDER_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
        setAgreementReminder(prev => ({ ...prev, ...Object.fromEntries(AGREEMENT_REMINDER_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
        prospectReminderSavedRef.current = {
          enabled: map['prospect_reminder_enabled'] ?? prospectReminderSavedRef.current.enabled,
          max: map['prospect_reminder_max'] ?? prospectReminderSavedRef.current.max,
        };
        agreementReminderSavedRef.current = {
          enabled: map['agreement_reminder_enabled'] ?? agreementReminderSavedRef.current.enabled,
          max: map['agreement_reminder_max'] ?? agreementReminderSavedRef.current.max,
        };
        setGeneralTerms(map['agreement_general_terms'] ?? '');
        // F4-3: Parse membership reminder days
        const membDaysStr = map['reminder_days_before_prolongation'] || '14,7,3';
        const membDaysArr = membDaysStr.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
        setMembReminderDays({ 14: membDaysArr.includes(14), 7: membDaysArr.includes(7), 3: membDaysArr.includes(3) });
        // F4-8b: Expiry hours
        setExpiryHours(map['pending_confirmation_expiry_hours'] || '48');
        // F4-4.1: dagcap broadcasts
        setMaxBroadcasts(map['max_broadcasts_per_day'] || '10');
        // F4-10: betaalmethoden systeembreed
        setPaymentMethods(prev => ({ ...prev, ...Object.fromEntries(PAYMENT_METHOD_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
        // Weekmodel credits
        setCreditExpiry(parseExpiryInterval(map['credit_default_expiry_interval']));
        setCreditWarningDays(map['credit_session_warning_days'] || '3');
        // Fitness tracking deel 7
        setUnloggedReminder(prev => ({ ...prev, ...Object.fromEntries(UNLOGGED_REMINDER_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
        setSelfLoggingEnabled(map[SELF_LOGGING_KEY] === 'true');
      } catch (err) {
        showError(t('instellingen.foutLaden') + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isAdmin]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const rows = Object.entries(fields)
        .filter(([key]) => !['company_logo_url'].includes(key))
        .map(([key, value]) => ({ key, value }));

      await upsertSettings(rows);
      showSuccess(t('instellingen.opgeslagen'));
    } catch (err) {
      showError(t('instellingen.foutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  // F4-10 Stap 2: live telling actieve abonnementen bij het uitschakelen van
  // een betaalmethode — puur informatief, blokkeert het uitschakelen niet.
  async function handlePaymentMethodToggle(method, checked) {
    setPaymentMethods(p => ({ ...p, [`payment_method_${method}_enabled`]: checked ? 'true' : 'false' }));

    if (checked) {
      setActiveMembershipCounts(prev => ({ ...prev, [method]: null }));
      return;
    }

    try {
      const { count, error: err } = await supabase
        .from('client_memberships')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'active')
        .eq('payment_method', method);
      if (err) throw err;
      setActiveMembershipCounts(prev => ({ ...prev, [method]: count ?? 0 }));
    } catch (err) {
      console.error('Kon actieve abonnementen niet tellen:', err);
    }
  }

  async function handleSaveEmailAuto(e) {
    e.preventDefault();
    setSavingEmailAuto(true);
    try {
      const rows = Object.entries(emailAuto).map(([key, value]) => ({ key, value }));
      await upsertSettings(rows);
      showSuccess(t('instellingen.emailAutomatiseringOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.foutEmailAutomatisering') + err.message);
    } finally {
      setSavingEmailAuto(false);
    }
  }

  // Onderdeel 3: bij het verhogen/instellen van het maximum moet voor elke
  // index 1..max een tekst (onderwerp + introtekst) bestaan — anders blokkeert
  // het opslaan met een modal i.p.v. een halve/foute automatische mail later.
  async function checkReminderTextsComplete(type, max) {
    if (!max || max < 1) return true;
    const { data, error } = await supabase
      .from('reminder_texts')
      .select('reminder_number, subject, intro')
      .eq('reminder_type', type)
      .gte('reminder_number', 1)
      .lte('reminder_number', max);
    if (error) throw error;
    const byNumber = {};
    (data || []).forEach(r => { byNumber[r.reminder_number] = r; });
    for (let i = 1; i <= max; i++) {
      const row = byNumber[i];
      if (!row || !row.subject?.trim() || !row.intro?.trim()) return false;
    }
    return true;
  }

  async function handleSaveProspectReminder(e) {
    e.preventDefault();
    setSavingProspectReminder(true);
    try {
      const max = parseInt(prospectReminder.prospect_reminder_max || '0', 10);
      const prevMax = parseInt(prospectReminderSavedRef.current.max || '0', 10);
      const enabledNow = prospectReminder.prospect_reminder_enabled === 'true';
      const enabledBefore = prospectReminderSavedRef.current.enabled === 'true';
      // Onderdeel 3: alleen bij verhogen van het max en/of inschakelen mag de
      // tekst-volledigheid het opslaan blokkeren — verlagen/uitschakelen moet
      // altijd lukken, anders is er geen manier meer om de automatisering uit
      // te zetten (self-lock).
      if (max > prevMax || (enabledNow && !enabledBefore)) {
        const complete = await checkReminderTextsComplete('prospect', max);
        if (!complete) {
          setMissingTextsModal('prospect');
          return;
        }
      }
      const rows = Object.entries(prospectReminder).map(([key, value]) => ({ key, value }));
      await upsertSettings(rows);
      prospectReminderSavedRef.current = { enabled: prospectReminder.prospect_reminder_enabled, max: prospectReminder.prospect_reminder_max };
      showSuccess(t('instellingen.prospectHerinneringenOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.foutProspectHerinneringen') + err.message);
    } finally {
      setSavingProspectReminder(false);
    }
  }

  async function handleSaveAgreementReminder(e) {
    e.preventDefault();
    setSavingAgreementReminder(true);
    try {
      const max = parseInt(agreementReminder.agreement_reminder_max || '0', 10);
      const prevMax = parseInt(agreementReminderSavedRef.current.max || '0', 10);
      const enabledNow = agreementReminder.agreement_reminder_enabled === 'true';
      const enabledBefore = agreementReminderSavedRef.current.enabled === 'true';
      // Onderdeel 3: zie toelichting in handleSaveProspectReminder hierboven.
      if (max > prevMax || (enabledNow && !enabledBefore)) {
        const complete = await checkReminderTextsComplete('agreement', max);
        if (!complete) {
          setMissingTextsModal('agreement');
          return;
        }
      }
      const rows = Object.entries(agreementReminder).map(([key, value]) => ({ key, value }));
      await upsertSettings(rows);
      agreementReminderSavedRef.current = { enabled: agreementReminder.agreement_reminder_enabled, max: agreementReminder.agreement_reminder_max };
      showSuccess(t('instellingen.overeenkomstHerinneringenOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.foutOvereenkomstHerinneringen') + err.message);
    } finally {
      setSavingAgreementReminder(false);
    }
  }

  async function handleSaveGeneralTerms(e) {
    e.preventDefault();
    setSavingGeneralTerms(true);
    try {
      await upsertSettings([{ key: 'agreement_general_terms', value: generalTerms }]);
      showSuccess(t('instellingen.algemeeneVoorwaardenOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.foutAlgemeneVoorwaarden') + err.message);
    } finally {
      setSavingGeneralTerms(false);
    }
  }

  async function handleSaveMembReminder(e) {
    e.preventDefault();
    setSavingMembReminder(true);
    try {
      const selected = Object.entries(membReminderDays).filter(([, v]) => v).map(([k]) => k);
      const value = selected.sort((a, b) => Number(b) - Number(a)).join(',');
      await upsertSettings([{ key: 'reminder_days_before_prolongation', value }]);
      showSuccess(t('instellingen.membReminderOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.membReminderFout') + err.message);
    } finally {
      setSavingMembReminder(false);
    }
  }

  async function handleSaveExpiry(e) {
    e.preventDefault();
    setSavingExpiry(true);
    try {
      const hours = parseInt(expiryHours, 10);
      if (isNaN(hours) || hours < 1) throw new Error('Minimaal 1 uur');
      await upsertSettings([{ key: 'pending_confirmation_expiry_hours', value: String(hours) }]);
      showSuccess(t('instellingen.expiryOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.expiryFout') + err.message);
    } finally {
      setSavingExpiry(false);
    }
  }

  async function handleSaveBroadcasts(e) {
    e.preventDefault();
    setSavingBroadcasts(true);
    try {
      const cap = parseInt(maxBroadcasts, 10);
      if (isNaN(cap) || cap < 1) throw new Error('Minimaal 1');
      await upsertSettings([{ key: 'max_broadcasts_per_day', value: String(cap) }]);
      showSuccess(t('instellingen.broadcastsOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.broadcastsFout') + err.message);
    } finally {
      setSavingBroadcasts(false);
    }
  }

  async function handleSavePaymentMethods(e) {
    e.preventDefault();
    setSavingPaymentMethods(true);
    try {
      const rows = Object.entries(paymentMethods).map(([key, value]) => ({ key, value }));
      await upsertSettings(rows);
      showSuccess(t('instellingen.betaalmethodenOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.betaalmethodenFout') + err.message);
    } finally {
      setSavingPaymentMethods(false);
    }
  }

  async function handleSaveCredits(e) {
    e.preventDefault();
    const aantal = parseInt(creditExpiry.aantal, 10);
    const dagen = parseInt(creditWarningDays, 10);
    if (!Number.isInteger(aantal) || aantal < 1) {
      showError(t('instellingen.creditsVervalOngeldig'));
      return;
    }
    if (!Number.isInteger(dagen) || dagen < 0) {
      showError(t('instellingen.creditsWaarschuwingOngeldig'));
      return;
    }
    setSavingCredits(true);
    try {
      const rows = [
        { key: 'credit_default_expiry_interval', value: `${aantal} ${creditExpiry.eenheid}` },
        { key: 'credit_session_warning_days', value: String(dagen) },
      ];
      await upsertSettings(rows);
      showSuccess(t('instellingen.creditsOpgeslagen'));
    } catch (err) {
      showError(t('instellingen.creditsFout') + err.message);
    } finally {
      setSavingCredits(false);
    }
  }

  // Onderdeel 14 — wijzigen via de RPC, niet via de settings-upsert. De RPC
  // weigert alles behalve superadmin; de knop hieronder is alleen gemak, de
  // beveiliging zit in de database.
  async function handleToggleSelfLogging(nieuweWaarde) {
    setSavingSelfLogging(true);
    try {
      const { data, error } = await supabase.rpc('set_client_self_logging_enabled', { p_enabled: nieuweWaarde });
      if (error) throw error;
      setSelfLoggingEnabled(data === true);
      showSuccess(t('instellingen.opgeslagen'));
    } catch (err) {
      showError(t('instellingen.foutOpslaan') + errorText(err, { scope: 'Instellingen.selfLogging' }));
    } finally {
      setSavingSelfLogging(false);
    }
  }

  async function handleSaveUnloggedReminder(e) {
    e.preventDefault();
    const delayHours = parseInt(unloggedReminder.unlogged_session_reminder_delay_hours, 10);
    if (!Number.isInteger(delayHours) || delayHours < 0) {
      showError(t('instellingen.unloggedReminderVertragingOngeldig'));
      return;
    }
    setSavingUnloggedReminder(true);
    try {
      const rows = [
        { key: 'unlogged_session_reminder_enabled', value: unloggedReminder.unlogged_session_reminder_enabled },
        { key: 'unlogged_session_reminder_delay_hours', value: String(delayHours) },
        { key: 'unlogged_session_reminder_frequency', value: unloggedReminder.unlogged_session_reminder_frequency },
      ];
      await upsertSettings(rows);
      showSuccess(t('instellingen.opgeslagen'));
    } catch (err) {
      showError(t('instellingen.foutOpslaan') + err.message);
    } finally {
      setSavingUnloggedReminder(false);
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showError(t('instellingen.logoFoutGrootte'));
      return;
    }
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      showError(t('instellingen.logoFoutType'));
      return;
    }

    setUploading(true);
    try {
      const { error: uploadErr } = await supabase.storage
        .from('company-assets')
        .upload('logo', file, { upsert: true, contentType: file.type });
      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage
        .from('company-assets')
        .getPublicUrl('logo');

      const logoUrl = urlData.publicUrl;

      await upsertSettings([{ key: 'company_logo_url', value: logoUrl }]);

      setFields(prev => ({ ...prev, company_logo_url: logoUrl }));
      showSuccess(t('instellingen.opgeslagen'));
    } catch (err) {
      showError(t('instellingen.logoFoutUpload') + err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  // ── Locatiebeheer functies ──
  async function loadLocations() {
    setLocationsLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('locations')
        .select('id, name, street, house_number, postal_code, city, address, image_url, is_active')
        .order('name');
      if (err) throw err;
      setLocations(data || []);
    } catch (err) {
      showError(t('instellingen.locatieFoutLaden') + err.message);
    } finally {
      setLocationsLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) loadLocations();
  }, [isAdmin]);

  // Vul form-velden wanneer modal opent voor bewerken
  useEffect(() => {
    if (editingLoc && locModal) {
      setLocForm({
        name: editingLoc.name || '',
        street: editingLoc.street || '',
        house_number: editingLoc.house_number || '',
        postal_code: editingLoc.postal_code || '',
        city: editingLoc.city || '',
        image_url: editingLoc.image_url || '',
      });
    }
  }, [editingLoc, locModal]);

  function openNewLocation() {
    setEditingLoc(null);
    setLocForm({ name: '', street: '', house_number: '', postal_code: '', city: '', image_url: '' });
    setLocModal(true);
  }

  function openEditLocation(loc) {
    setEditingLoc(loc);
    setLocModal(true);
  }

  async function saveLocation(e) {
    e.preventDefault();
    if (!locForm.name.trim()) {
      showError(t('instellingen.locatieNaamVerplicht'));
      return;
    }
    setSavingLoc(true);
    try {
      const payload = {
        name: locForm.name.trim(),
        street: locForm.street.trim() || null,
        house_number: locForm.house_number.trim() || null,
        postal_code: locForm.postal_code.trim() || null,
        city: locForm.city.trim() || null,
        image_url: locForm.image_url || null,
      };
      if (editingLoc) {
        const { data: updRow, error: err } = await supabase
          .from('locations')
          .update(payload)
          .eq('id', editingLoc.id)
          .select('id')
          .maybeSingle();
        if (err) throw err;
        if (!updRow) { showError(t('common.wijzigingNietOpgeslagen')); return; }
      } else {
        const { error: err } = await supabase.from('locations').insert(payload);
        if (err) throw err;
      }
      showSuccess(t('instellingen.opgeslagen'));
      await loadLocations();
      setLocModal(false);
    } catch (err) {
      showError(t('instellingen.locatieFoutOpslaan') + err.message);
    } finally {
      setSavingLoc(false);
    }
  }

  async function handleLocationImageUpload(file) {
    if (!file) return;
    setLocationImageUploading(true);
    try {
      const ownerId = editingLoc?.id || `new-${Date.now()}`;
      const url = await uploadTrainingImage(supabase, 'locations', ownerId, file);
      setLocForm(current => ({ ...current, image_url: url }));
    } catch {
      showError(t('instellingen.afbeeldingUploadFout'));
    } finally {
      setLocationImageUploading(false);
    }
  }

  function toggleLocationActive(loc) {
    setLocToggleConfirm(loc);
  }

  async function handleLocationToggleConfirm() {
    const loc = locToggleConfirm;
    setLocToggleConfirm(null);
    if (!loc) return;
    try {
      const { data: updRow, error: err } = await supabase
        .from('locations')
        .update({ is_active: !loc.is_active })
        .eq('id', loc.id)
        .select('id')
        .maybeSingle();
      if (err) throw err;
      if (!updRow) { showError(t('common.wijzigingNietOpgeslagen')); return; }
      await loadLocations();
      showSuccess(t('instellingen.opgeslagen'));
    } catch (err) {
      showError(t('instellingen.locatieFoutToggle') + err.message);
    }
  }

  // Alleen admin-secties wachten op de settings-query; de feed-sectie heeft eigen loading state
  if (view === 'organization' && loading && isAdmin) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t(view === 'profile' ? 'beheer.profielTitel' : view === 'locations' ? 'beheer.locatiesTitel' : 'beheer.organisatieTitel')}</h1>
          <p className="page-subtitle">{t(view === 'profile' ? 'beheer.profielSubtitel' : view === 'locations' ? 'beheer.locatiesSubtitel' : 'beheer.organisatieSubtitel')}</p>
        </div>
      </div>

      {view === 'organization' && (
        <nav className="settings-section-nav" aria-label={t('beheer.organisatieNavigatie')}>
          {ORGANISATION_SECTIONS.map(item => (
            <NavLink key={item.id} to={`/beheer/organisatie/${item.id}`} className={({ isActive }) => isActive ? 'is-active' : ''}>
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>
      )}

      {view === 'profile' && <>
      {/* Sectie 0: Google Agenda koppeling (voor alle coaches) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('coachInstellingen.sectionCalFeed')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('coachInstellingen.intro')}
        </p>

        {feedError && <div className="error-msg" style={{ marginBottom: 12 }}>{feedError}</div>}

        {feedLoading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : feedUrl ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>
                {t('coachInstellingen.urlLabel')}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-input"
                  value={feedUrl}
                  readOnly
                  onClick={e => e.target.select()}
                  style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleCopy}
                  style={{ flexShrink: 0, minWidth: 100 }}
                >
                  {copied ? t('coachInstellingen.gekopieerd') : t('coachInstellingen.kopierenKnop')}
                </button>
              </div>
            </div>

            <div style={{
              background: 'var(--color-bg-secondary, #f5f5f5)',
              borderRadius: 8,
              padding: '16px 20px',
              marginBottom: 20,
            }}>
              <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, marginTop: 0 }}>
                {t('coachInstellingen.instructieTitel')}
              </p>
              {[
                t('coachInstellingen.stap1'),
                t('coachInstellingen.stap2'),
                t('coachInstellingen.stap3'),
                t('coachInstellingen.stap4'),
                t('coachInstellingen.stap5'),
              ].map((stap, i) => (
                <p key={i} style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 6px' }}>
                  {stap}
                </p>
              ))}
            </div>

            {showConfirm ? (
              <div style={{
                background: '#fff3e0',
                border: '1px solid #ffb74d',
                borderRadius: 8,
                padding: 16,
                marginBottom: 8,
              }}>
                <p style={{ fontWeight: 600, fontSize: 14, margin: '0 0 8px', color: '#e65100' }}>
                  ⚠ {t('coachInstellingen.vernieuwenKnop')}
                </p>
                <p style={{ fontSize: 13, color: '#795548', margin: '0 0 12px', lineHeight: 1.5 }}>
                  {t('coachInstellingen.vernieuwenWaarschuwing')}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn"
                    onClick={() => setShowConfirm(false)}
                    style={{ background: 'none', border: '1px solid #ccc' }}
                  >
                    {t('coachInstellingen.vernieuwenAnnuleren')}
                  </button>
                  <button
                    className="btn"
                    onClick={doGenerate}
                    disabled={generating}
                    style={{ background: '#e65100', color: '#fff', border: 'none', opacity: generating ? 0.6 : 1 }}
                  >
                    {generating ? t('coachInstellingen.generaterenBezig') : t('coachInstellingen.vernieuwenBevestigen')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="btn btn-ghost"
                onClick={() => setShowConfirm(true)}
                style={{ color: '#e65100', borderColor: '#e65100' }}
              >
                {t('coachInstellingen.vernieuwenKnop')}
              </button>
            )}
          </>
        ) : (
          <div>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
              {t('coachInstellingen.geenToken')}
            </p>
            <button
              className="btn btn-primary"
              onClick={doGenerate}
              disabled={generating}
            >
              {generating ? t('coachInstellingen.generaterenBezig') : t('coachInstellingen.generaterenKnop')}
            </button>
          </div>
        )}
      </div>

      {/* Sectie 0B: Privé-agenda koppelen (ICS-abonnement, voor alle coaches) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('privateAgendaKoppeling.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('privateAgendaKoppeling.intro')}
        </p>

        {privCalError && <div className="error-msg" style={{ marginBottom: 12 }}>{privCalError}</div>}

        {privCalLoading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : (
          <form onSubmit={handleSavePrivCal}>
            <div className="form-group">
              <label className="form-label">{t('privateAgendaKoppeling.urlLabel')}</label>
              <input
                className="form-input"
                type="url"
                value={privCal.ics_url}
                onChange={e => setPrivCal(p => ({ ...p, ics_url: e.target.value }))}
                placeholder="https://calendar.google.com/calendar/ical/.../basic.ics"
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </div>

            <div style={{
              background: 'var(--color-bg-secondary, #f5f5f5)',
              borderRadius: 8,
              padding: '16px 20px',
              marginBottom: 20,
            }}>
              <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, marginTop: 0 }}>
                {t('privateAgendaKoppeling.instructieTitel')}
              </p>
              {[
                t('privateAgendaKoppeling.instructieGoogle'),
                t('privateAgendaKoppeling.instructieOutlook'),
                t('privateAgendaKoppeling.instructieIcloud'),
              ].map((stap, i) => (
                <p key={i} style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 6px' }}>
                  {stap}
                </p>
              ))}
            </div>

            <div style={{ display: 'grid', gap: 20, marginBottom: 20 }}>
              <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    id="share_details"
                    checked={privCal.share_details}
                    onChange={e => setPrivCal(p => ({ ...p, share_details: e.target.checked }))}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <label htmlFor="share_details" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                    {t('privateAgendaKoppeling.labelShareDetails')}
                  </label>
                </div>
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                  {t('privateAgendaKoppeling.hintShareDetails')}
                </p>
              </div>

            </div>

            {privCalStatus.last_synced_at && (
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                {t('privateAgendaKoppeling.laatsteSyncLabel')}{' '}
                {new Date(privCalStatus.last_synced_at).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' })}
                {' — '}
                {privCalStatus.last_sync_status === 'success'
                  ? t('privateAgendaKoppeling.syncStatusSuccess')
                  : t('privateAgendaKoppeling.syncStatusError') + (privCalStatus.last_sync_message ? `: ${privCalStatus.last_sync_message}` : '')}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleSyncNow}
                disabled={privCalSyncing || !privCal.ics_url.trim()}
              >
                {privCalSyncing ? t('privateAgendaKoppeling.syncBezig') : t('privateAgendaKoppeling.syncNuKnop')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={privCalSaving}>
                {privCalSaving ? t('auth.loading') : t('privateAgendaKoppeling.opslaanKnop')}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Sectie 0C: Meldingen (push_preferences, C4, zichtbaar voor alle coach-rollen) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('pushCat.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('pushCat.introCoach')}
        </p>

        {pushPrefs && (
          <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <input
                type="checkbox"
                id="pushcat_client_activity"
                checked={pushPrefs.client_activity ?? true}
                onChange={e => handleTogglePushCat('client_activity', e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <label htmlFor="pushcat_client_activity" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                {t('pushCat.clientActivityLabel')}
              </label>
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
              {t('pushCat.clientActivityDesc')}
            </p>
          </div>
        )}

        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', margin: 0 }}>
          {t('pushCat.alwaysOn')}
        </p>
      </div>

      {/* Sectie 0D: Opzeg-mail-instelling (Onderdeel 1, zichtbaar voor alle coach-rollen) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('opzegMailInstelling.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('opzegMailInstelling.intro')}
        </p>

        {!cancelEmailLoading && (
          <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="checkbox"
                id="receives_cancellation_emails"
                checked={receivesCancellationEmails}
                disabled={cancelEmailSaving}
                onChange={e => handleToggleCancellationEmails(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <label htmlFor="receives_cancellation_emails" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                {t('opzegMailInstelling.label')}
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Sectie 0E: Korte introductie (2026-08-01, zichtbaar voor alle coach-rollen) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('coachBio.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('coachBio.intro')}
        </p>

        {!cancelEmailLoading && (
          <>
            <div className="form-group">
              <label className="form-label">{t('coachBio.fotoLabel')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" style={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 88, height: 88, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'var(--color-primary-light)', color: 'var(--color-primary)', fontSize: 28, fontWeight: 800 }}>
                    {(user?.email || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <label className="btn btn-ghost" style={{ cursor: avatarUploading ? 'wait' : 'pointer' }}>
                  {avatarUploading ? t('coachBio.fotoUploaden') : t('coachBio.fotoKiezen')}
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={avatarUploading} onChange={e => handleAvatarUpload(e.target.files?.[0])} />
                </label>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 6 }}>{t('coachBio.fotoHint')}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('coachBio.label')}</label>
              <textarea
                className="form-input"
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder={t('coachBio.placeholder')}
                rows={3}
                maxLength={BIO_MAX_LENGTH}
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                {t('coachBio.counter', { count: bio.length, max: BIO_MAX_LENGTH })}
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleSaveBio} disabled={bioSaving}>
              {bioSaving ? t('auth.loading') : t('coachBio.opslaanKnop')}
            </button>
          </>
        )}
      </div>

      {/* Sectie 0F: Wachtwoord wijzigen (zichtbaar voor alle coach-rollen) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('wachtwoordWijzigen.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('wachtwoordWijzigen.intro')}
        </p>
        <form onSubmit={handleChangePassword}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">{t('wachtwoordWijzigen.labelNieuw')}</label>
              <input
                className="form-input"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder={t('wachtwoordWijzigen.placeholderNieuw')}
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('wachtwoordWijzigen.labelHerhaal')}</label>
              <input
                className="form-input"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={changingPassword}>
              {changingPassword ? t('auth.loading') : t('wachtwoordWijzigen.opslaanKnop')}
            </button>
          </div>
        </form>
      </div>

      </>}

      {isAdmin && view === 'organization' && <div className="settings-filter" data-active={activeOrganisationSection}> {/* Organisatie-instellingen */}

      {/* Sectie 1: Bedrijfsgegevens */}
      <div className="card" data-settings-section="bedrijf" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>{t('instellingen.bedrijfsgegevens')}</h2>
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelBedrijfsnaam')}</label>
              <input
                className="form-input"
                value={fields.company_name}
                onChange={e => setFields(p => ({ ...p, company_name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelEmail')}</label>
              <input
                className="form-input"
                type="email"
                value={fields.company_email}
                onChange={e => setFields(p => ({ ...p, company_email: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelAdres')}</label>
              <input
                className="form-input"
                value={fields.company_address}
                onChange={e => setFields(p => ({ ...p, company_address: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelPostcode')}</label>
              <input
                className="form-input"
                value={fields.company_postcode}
                onChange={e => setFields(p => ({ ...p, company_postcode: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelStad')}</label>
              <input
                className="form-input"
                value={fields.company_city}
                onChange={e => setFields(p => ({ ...p, company_city: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelTelefoon')}</label>
              <input
                className="form-input"
                value={fields.company_phone}
                onChange={e => setFields(p => ({ ...p, company_phone: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelKvk')}</label>
              <input
                className="form-input"
                value={fields.company_kvk}
                onChange={e => setFields(p => ({ ...p, company_kvk: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelBtw')}</label>
              <input
                className="form-input"
                value={fields.company_btw}
                onChange={e => setFields(p => ({ ...p, company_btw: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('instellingen.labelIban')}</label>
              <input
                className="form-input"
                value={fields.company_iban}
                onChange={e => setFields(p => ({ ...p, company_iban: e.target.value }))}
                placeholder="NL00 BANK 0000 0000 00"
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('instellingen.labelEmailFrom')}</label>
              <input
                className="form-input"
                type="email"
                value={fields.email_from}
                onChange={e => setFields(p => ({ ...p, email_from: e.target.value }))}
                placeholder="info@app.venema.email"
              />
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4, marginBottom: 0 }}>
                {t('instellingen.hintEmailFrom')}
              </p>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('instellingen.labelEmailInvoices')}</label>
              <input
                className="form-input"
                type="email"
                value={fields.email_invoices}
                onChange={e => setFields(p => ({ ...p, email_invoices: e.target.value }))}
                placeholder="facturen@app.venema.email"
              />
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4, marginBottom: 0 }}>
                {t('instellingen.hintEmailInvoices')}
              </p>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('instellingen.labelEmailAgreements')}</label>
              <input
                className="form-input"
                type="email"
                value={fields.email_agreements}
                onChange={e => setFields(p => ({ ...p, email_agreements: e.target.value }))}
                placeholder="overeenkomsten@app.venema.email"
              />
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4, marginBottom: 0 }}>
                {t('instellingen.hintEmailAgreements')}
              </p>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('instellingen.labelEmailNotifications')}</label>
              <input
                className="form-input"
                type="email"
                value={fields.email_notifications}
                onChange={e => setFields(p => ({ ...p, email_notifications: e.target.value }))}
                placeholder="noreply@app.venema.email"
              />
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4, marginBottom: 0 }}>
                {t('instellingen.hintEmailNotifications')}
              </p>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('instellingen_urls.labelGymUrl')}</label>
              <input
                className="form-input"
                type="url"
                value={fields.gym_url}
                onChange={e => setFields(p => ({ ...p, gym_url: e.target.value }))}
                placeholder={t('instellingen_urls.placeholderGymUrl')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen_urls.labelAppStoreUrl')}</label>
              <input
                className="form-input"
                type="url"
                value={fields.app_store_url}
                onChange={e => setFields(p => ({ ...p, app_store_url: e.target.value }))}
                placeholder={t('instellingen_urls.placeholderAppStoreUrl')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen_urls.labelPlayStoreUrl')}</label>
              <input
                className="form-input"
                type="url"
                value={fields.play_store_url}
                onChange={e => setFields(p => ({ ...p, play_store_url: e.target.value }))}
                placeholder={t('instellingen_urls.placeholderPlayStoreUrl')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('instellingen.labelBetalingstermijn')}</label>
              <input
                className="form-input"
                type="number"
                min="1"
                value={fields.company_payment_term_days}
                onChange={e => setFields(p => ({ ...p, company_payment_term_days: e.target.value }))}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }} className="form-group">
              <label className="form-label">{t('instellingen.labelFactuurvoetnoot')}</label>
              <textarea
                className="form-input"
                rows={3}
                value={fields.invoice_footer}
                onChange={e => setFields(p => ({ ...p, invoice_footer: e.target.value }))}
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Verouderde snelkoppeling blijft alleen in de DOM voor backwards-compatible logica. */}
      <div className="card" data-settings-section="standalone" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{t('abonnementen.sectionTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 16, fontSize: 13 }}>
          Beheer de abonnementstypes en pakketten die beschikbaar zijn voor cliënten.
        </p>
        <Link to="/beheer/abonnementen" className="btn btn-secondary btn-sm">
          {t('abonnementen.beheerLink')}
        </Link>
      </div>

      {/* Communicatieteksten hebben een zelfstandige beheerroute. */}
      <div className="card" data-settings-section="standalone" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{t('instellingen.factuurtekstenSectionTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 16, fontSize: 13 }}>
          {t('instellingen.factuurtekstenSectionSubtitel')}
        </p>
        <Link to="/beheer/communicatie/facturen" className="btn btn-secondary btn-sm">
          {t('instellingen.factuurtekstenBeheerLink')}
        </Link>
      </div>

      <div className="card" data-settings-section="standalone" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{t('instellingen.emailtekstenSectionTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 16, fontSize: 13 }}>
          {t('instellingen.emailtekstenSectionSubtitel')}
        </p>
        <Link to="/beheer/communicatie/email" className="btn btn-secondary btn-sm">
          {t('instellingen.emailtekstenBeheerLink')}
        </Link>
      </div>

      {/* Sectie 4: E-mail automatisering */}
      <div className="card" data-settings-section="automatisering" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.emailAutomatiseringTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
          {t('instellingen.emailAutomatiseringSubtitel')}
        </p>
        <form onSubmit={handleSaveEmailAuto}>
          <div style={{ display: 'grid', gap: 20, marginBottom: 20 }}>
            {/* Toggle 1: Herinnering automatisch */}
            <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id="email_auto_reminder"
                  checked={emailAuto.email_auto_reminder === 'true'}
                  onChange={e => setEmailAuto(p => ({ ...p, email_auto_reminder: e.target.checked ? 'true' : 'false' }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="email_auto_reminder" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t('instellingen.labelAutoReminder')}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t('instellingen.hintAutoReminder')}
              </p>
            </div>

            {/* Toggle 2: Aanmaning automatisch */}
            <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id="email_auto_final_notice"
                  checked={emailAuto.email_auto_final_notice === 'true'}
                  onChange={e => setEmailAuto(p => ({ ...p, email_auto_final_notice: e.target.checked ? 'true' : 'false' }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="email_auto_final_notice" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t('instellingen.labelAutoFinalNotice')}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t('instellingen.hintAutoFinalNotice')}
              </p>
            </div>

            {/* Toggle 2b: Facturen bij automatische verlengingen direct versturen */}
            <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id="email_auto_recurring_invoices"
                  checked={emailAuto.email_auto_recurring_invoices === 'true'}
                  onChange={e => {
                    if (e.target.checked) {
                      setAutoRecurringInvoicesConfirm(true);
                    } else {
                      setEmailAuto(p => ({ ...p, email_auto_recurring_invoices: 'false' }));
                    }
                  }}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="email_auto_recurring_invoices" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t('instellingen.labelAutoRecurringInvoices')}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t('instellingen.hintAutoRecurringInvoices')}
              </p>
            </div>

            {/* Toggle 3: Push notificatie bij aanmaning */}
            <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id="push_reminder_enabled"
                  checked={emailAuto.push_reminder_enabled === 'true'}
                  onChange={e => setEmailAuto(p => ({ ...p, push_reminder_enabled: e.target.checked ? 'true' : 'false' }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="push_reminder_enabled" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t('instellingen.labelPushEnabled')}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t('instellingen.hintPushEnabled')}
              </p>
            </div>

            {/* Veld 4: Push notificatie tekst */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('instellingen.labelPushText')}</label>
              <textarea
                className="form-input"
                rows={2}
                value={emailAuto.push_reminder_text}
                onChange={e => setEmailAuto(p => ({ ...p, push_reminder_text: e.target.value }))}
                style={{ resize: 'vertical' }}
              />
              <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>{t('instellingen.hintPushDatum')}</p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingEmailAuto}>
              {savingEmailAuto ? t('auth.loading') : t('instellingen.opslaanEmailAutomatisering')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie 4B: Prospect opvolgherinneringen */}
      <div className="card" data-settings-section="automatisering">
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.prospectHerinneringenTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
          {t('instellingen.prospectHerinneringenSubtitel')}
        </p>
        <form onSubmit={handleSaveProspectReminder}>
          <div style={{ display: 'grid', gap: 20, marginBottom: 20 }}>
            {/* Toggle: Herinneringen enabled */}
            <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id="prospect_reminder_enabled"
                  checked={prospectReminder.prospect_reminder_enabled === 'true'}
                  onChange={e => setProspectReminder(p => ({ ...p, prospect_reminder_enabled: e.target.checked ? 'true' : 'false' }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="prospect_reminder_enabled" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t('instellingen.labelProspectReminderEnabled')}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t('instellingen.hintProspectReminderEnabled')}
              </p>
            </div>

            {/* Number input: Interval dagen */}
            <div>
              <label htmlFor="prospect_reminder_interval_days" className="form-label" style={{ fontSize: 14, fontWeight: 600 }}>
                {t('instellingen.labelProspectReminderInterval')}
              </label>
              <input
                type="number"
                id="prospect_reminder_interval_days"
                className="form-input"
                value={prospectReminder.prospect_reminder_interval_days}
                onChange={e => setProspectReminder(p => ({ ...p, prospect_reminder_interval_days: e.target.value }))}
                min="1"
                style={{ maxWidth: 120 }}
              />
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                {t('instellingen.hintProspectReminderInterval')}
              </p>
            </div>

            {/* Number input: Max herinneringen */}
            <div>
              <label htmlFor="prospect_reminder_max" className="form-label" style={{ fontSize: 14, fontWeight: 600 }}>
                {t('instellingen.labelProspectReminderMax')}
              </label>
              <input
                type="number"
                id="prospect_reminder_max"
                className="form-input"
                value={prospectReminder.prospect_reminder_max}
                onChange={e => setProspectReminder(p => ({ ...p, prospect_reminder_max: e.target.value }))}
                min="0"
                style={{ maxWidth: 120 }}
              />
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                {t('instellingen.hintProspectReminderMax')}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingProspectReminder}>
              {savingProspectReminder ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie 5: Overeenkomst herinneringen (UITBREIDING 9) */}
      <div className="card" data-settings-section="automatisering" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>{t('instellingen.overeenkomstHerinneringenTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          {t('instellingen.overeenkomstHerinneringenUitleg')}
        </p>
        <form onSubmit={handleSaveAgreementReminder} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="checkbox"
                id="agreement_reminder_enabled"
                checked={agreementReminder.agreement_reminder_enabled === 'true'}
                onChange={e => setAgreementReminder(p => ({ ...p, agreement_reminder_enabled: e.target.checked ? 'true' : 'false' }))}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="agreement_reminder_enabled" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                {t('instellingen.labelAgreementReminderEnabled')}
              </label>
            </div>
          </div>

          <div>
            <label htmlFor="agreement_reminder_interval_days" className="form-label" style={{ fontSize: 14, fontWeight: 600 }}>
              {t('instellingen.labelAgreementReminderInterval')}
            </label>
            <input
              type="number"
              id="agreement_reminder_interval_days"
              className="form-input"
              value={agreementReminder.agreement_reminder_interval_days}
              onChange={e => setAgreementReminder(p => ({ ...p, agreement_reminder_interval_days: e.target.value }))}
              min="1"
              style={{ maxWidth: 120 }}
            />
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, lineHeight: 1.5 }}>
              {t('instellingen.hintAgreementReminderInterval')}
            </p>
          </div>

          <div>
            <label htmlFor="agreement_reminder_max" className="form-label" style={{ fontSize: 14, fontWeight: 600 }}>
              {t('instellingen.labelAgreementReminderMax')}
            </label>
            <input
              type="number"
              id="agreement_reminder_max"
              className="form-input"
              value={agreementReminder.agreement_reminder_max}
              onChange={e => setAgreementReminder(p => ({ ...p, agreement_reminder_max: e.target.value }))}
              min="0"
              style={{ maxWidth: 120 }}
            />
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, lineHeight: 1.5 }}>
              {t('instellingen.hintAgreementReminderMax')}
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingAgreementReminder}>
              {savingAgreementReminder ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie: Centrale algemene overeenkomstvoorwaarden */}
      <div className="card" data-settings-section="juridisch" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.algemeeneVoorwaardenTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 16, fontSize: 14, lineHeight: 1.6 }}>
          {t('instellingen.algemeeneVoorwaardenUitleg')}
        </p>
        <form onSubmit={handleSaveGeneralTerms}>
          <textarea
            className="form-input"
            rows={10}
            value={generalTerms}
            onChange={e => setGeneralTerms(e.target.value)}
            placeholder={t('instellingen.algemeeneVoorwaardenPlaceholder')}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 14, resize: 'vertical', marginBottom: 16 }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingGeneralTerms}>
              {savingGeneralTerms ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie: Standaard herinneringen abonnementsverlenging (F4-3) */}
      <div className="card" data-settings-section="automatisering" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.membReminderTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          {t('instellingen.membReminderUitleg')}
        </p>
        <form onSubmit={handleSaveMembReminder}>
          <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
            {[14, 7, 3].map(d => (
              <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={membReminderDays[d] || false}
                  onChange={e => setMembReminderDays(prev => ({ ...prev, [d]: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{d} {t('instellingen.membReminderDagen')}</span>
              </label>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 16, lineHeight: 1.5, fontStyle: 'italic' }}>
            {t('instellingen.membReminderWettelijk')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingMembReminder}>
              {savingMembReminder ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie: Vervaltijd uitnodigingen */}
      <div className="card" data-settings-section="automatisering">
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.expiryTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          {t('instellingen.expiryUitleg')}
        </p>
        <form onSubmit={handleSaveExpiry}>
          <div className="form-group" style={{ maxWidth: 200 }}>
            <label className="form-label">{t('instellingen.expiryLabel')}</label>
            <input
              type="number"
              className="form-input"
              value={expiryHours}
              onChange={e => setExpiryHours(e.target.value)}
              min="1"
              max="168"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingExpiry}>
              {savingExpiry ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie: Dagcap broadcasts (F4-4.1) */}
      <div className="card" data-settings-section="automatisering">
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.broadcastsTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          {t('instellingen.broadcastsUitleg')}
        </p>
        <form onSubmit={handleSaveBroadcasts}>
          <div className="form-group" style={{ maxWidth: 200 }}>
            <label className="form-label">{t('instellingen.broadcastsLabel')}</label>
            <input
              type="number"
              className="form-input"
              value={maxBroadcasts}
              onChange={e => setMaxBroadcasts(e.target.value)}
              min="1"
              max="1000"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingBroadcasts}>
              {savingBroadcasts ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie: Credits (weekmodel 2026-08-08) */}
      <div className="card" data-settings-section="credits" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.creditsTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          {t('instellingen.creditsUitleg')}
        </p>
        <form onSubmit={handleSaveCredits}>
          <div style={{ marginBottom: 20 }}>
            <label className="form-label" htmlFor="credit_expiry_aantal">{t('instellingen.creditsVervalLabel')}</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                id="credit_expiry_aantal"
                className="form-input"
                type="number"
                min="1"
                max="60"
                value={creditExpiry.aantal}
                onChange={e => setCreditExpiry(prev => ({ ...prev, aantal: e.target.value }))}
                style={{ width: 100 }}
              />
              <select
                className="form-input"
                aria-label={t('instellingen.creditsVervalEenheid')}
                value={creditExpiry.eenheid}
                onChange={e => setCreditExpiry(prev => ({ ...prev, eenheid: e.target.value }))}
                style={{ width: 160 }}
              >
                <option value="weeks">{t('instellingen.creditsEenheidWeken')}</option>
                <option value="mons">{t('instellingen.creditsEenheidMaanden')}</option>
              </select>
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {t('instellingen.creditsVervalHint')}
            </p>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="form-label" htmlFor="credit_warning_days">{t('instellingen.creditsWaarschuwingLabel')}</label>
            <input
              id="credit_warning_days"
              className="form-input"
              type="number"
              min="0"
              max="30"
              value={creditWarningDays}
              onChange={e => setCreditWarningDays(e.target.value)}
              style={{ width: 100 }}
            />
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {t('instellingen.creditsWaarschuwingHint')}
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingCredits}>
              {savingCredits ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie: Zelfstandig trainingen loggen (Onderdeel 14).
          Alleen zichtbaar voor superadmin — een admin_coach beheert wel de
          abonnementstype-default en de coach de cliëntoverride, maar niet de
          instance-brede hoofdschakelaar. */}
      {role === 'superadmin' && (
        <div className="card" data-settings-section="credits" style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.selfLoggingTitel')}</h2>
          <p className="text-muted" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
            {t('instellingen.selfLoggingUitleg')}
          </p>
          <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <input
                type="checkbox"
                id="client_self_logging_enabled"
                checked={selfLoggingEnabled}
                disabled={savingSelfLogging}
                onChange={e => handleToggleSelfLogging(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <label htmlFor="client_self_logging_enabled" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                {t('instellingen.selfLoggingLabel')}
              </label>
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
              {t('instellingen.selfLoggingHint')}
            </p>
          </div>
          <p className="text-muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0, lineHeight: 1.6 }}>
            {t('instellingen.selfLoggingLagen')}
          </p>
        </div>
      )}

      {/* Sectie: Niet-gelogde PT-sessie-herinnering (fitness tracking deel 7) */}
      <div className="card" data-settings-section="automatisering" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.unloggedReminderTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          {t('instellingen.unloggedReminderUitleg')}
        </p>
        <form onSubmit={handleSaveUnloggedReminder}>
          <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <input
                type="checkbox"
                id="unlogged_session_reminder_enabled"
                checked={unloggedReminder.unlogged_session_reminder_enabled === 'true'}
                onChange={e => setUnloggedReminder(p => ({ ...p, unlogged_session_reminder_enabled: e.target.checked ? 'true' : 'false' }))}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <label htmlFor="unlogged_session_reminder_enabled" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                {t('instellingen.unloggedReminderLabelEnabled')}
              </label>
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
              {t('instellingen.unloggedReminderHintEnabled')}
            </p>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="form-label" htmlFor="unlogged_reminder_delay">{t('instellingen.unloggedReminderLabelVertraging')}</label>
            <input
              id="unlogged_reminder_delay"
              className="form-input"
              type="number"
              min="0"
              max="72"
              value={unloggedReminder.unlogged_session_reminder_delay_hours}
              onChange={e => setUnloggedReminder(p => ({ ...p, unlogged_session_reminder_delay_hours: e.target.value }))}
              style={{ width: 100 }}
            />
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {t('instellingen.unloggedReminderHintVertraging')}
            </p>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="form-label" htmlFor="unlogged_reminder_frequency">{t('instellingen.unloggedReminderLabelFrequentie')}</label>
            <select
              id="unlogged_reminder_frequency"
              className="form-input"
              value={unloggedReminder.unlogged_session_reminder_frequency}
              onChange={e => setUnloggedReminder(p => ({ ...p, unlogged_session_reminder_frequency: e.target.value }))}
              style={{ width: 220 }}
            >
              <option value="once">{t('instellingen.unloggedReminderFrequentieEenmalig')}</option>
              <option value="weekly">{t('instellingen.unloggedReminderFrequentieWekelijks')}</option>
              <option value="biweekly">{t('instellingen.unloggedReminderFrequentieTweewekelijks')}</option>
            </select>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {t('instellingen.unloggedReminderHintFrequentie')}
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingUnloggedReminder}>
              {savingUnloggedReminder ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie: Betaalmethoden (F4-10) */}
      <div className="card" data-settings-section="facturatie" style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('instellingen.betaalmethodenTitel')}</h2>
        <p className="text-muted" style={{ marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          {t('instellingen.betaalmethodenUitleg')}
        </p>
        <form onSubmit={handleSavePaymentMethods}>
          <div style={{ display: 'grid', gap: 20, marginBottom: 20 }}>
            {/* Toggle: iDEAL */}
            <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id="payment_method_ideal_enabled"
                  checked={paymentMethods.payment_method_ideal_enabled === 'true'}
                  onChange={e => handlePaymentMethodToggle('ideal', e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="payment_method_ideal_enabled" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t('instellingen.betaalmethodenLabelIdeal')}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t('instellingen.betaalmethodenHintIdeal')}
              </p>
              {paymentMethods.payment_method_ideal_enabled !== 'true' && activeMembershipCounts.ideal > 0 && (
                <p style={{ fontSize: 12, color: 'var(--color-warning, #b45309)', marginLeft: 30, marginTop: 8, marginBottom: 0, lineHeight: 1.5, fontWeight: 600 }}>
                  {t(activeMembershipCounts.ideal === 1 ? 'instellingen.betaalmethodenWaarschuwingIdealEnkelvoud' : 'instellingen.betaalmethodenWaarschuwingIdealMeervoud', { aantal: activeMembershipCounts.ideal })}
                </p>
              )}
            </div>

            {/* Toggle: SEPA */}
            <div style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id="payment_method_sepa_enabled"
                  checked={paymentMethods.payment_method_sepa_enabled === 'true'}
                  onChange={e => handlePaymentMethodToggle('sepa', e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor="payment_method_sepa_enabled" className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t('instellingen.betaalmethodenLabelSepa')}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t('instellingen.betaalmethodenHintSepa')}
              </p>
              {paymentMethods.payment_method_sepa_enabled !== 'true' && activeMembershipCounts.sepa > 0 && (
                <p style={{ fontSize: 12, color: 'var(--color-warning, #b45309)', marginLeft: 30, marginTop: 8, marginBottom: 0, lineHeight: 1.5, fontWeight: 600 }}>
                  {t(activeMembershipCounts.sepa === 1 ? 'instellingen.betaalmethodenWaarschuwingSepaEnkelvoud' : 'instellingen.betaalmethodenWaarschuwingSepaMeervoud', { aantal: activeMembershipCounts.sepa })}
                </p>
              )}
              <div style={{ marginTop: 12, marginLeft: 30 }}>
                <label className="form-label" htmlFor="mollie_incassant_id" style={{ fontSize: 13 }}>
                  {t('instellingen.mollieIncassantIdLabel')}
                </label>
                <input
                  type="text"
                  id="mollie_incassant_id"
                  className="form-input"
                  value={paymentMethods.mollie_incassant_id}
                  onChange={e => setPaymentMethods(p => ({ ...p, mollie_incassant_id: e.target.value }))}
                  placeholder={t('instellingen.mollieIncassantIdPlaceholder')}
                  style={{ maxWidth: 320 }}
                />
                {!paymentMethods.mollie_incassant_id?.trim() && (
                  <p style={{ fontSize: 12, color: 'var(--color-warning, #b45309)', marginTop: 8, marginBottom: 0, lineHeight: 1.5, fontWeight: 600 }}>
                    {t('instellingen.mollieIncassantIdWaarschuwing')}
                  </p>
                )}
              </div>
            </div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 16, lineHeight: 1.5, fontStyle: 'italic' }}>
            {t('instellingen.betaalmethodenHandmatigInfo')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={savingPaymentMethods}>
              {savingPaymentMethods ? t('auth.loading') : t('instellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      {/* Sectie 6: Logo */}
      <div className="card" data-settings-section="branding">
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>{t('instellingen.logo')}</h2>

        {fields.company_logo_url ? (
          <div style={{ marginBottom: 16 }}>
            <p className="text-muted" style={{ marginBottom: 8, fontSize: 13 }}>{t('instellingen.logoHuidig')}</p>
            <img
              src={fields.company_logo_url}
              alt="Logo"
              style={{ maxWidth: 240, maxHeight: 80, objectFit: 'contain', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 8 }}
            />
          </div>
        ) : (
          <p className="text-muted" style={{ marginBottom: 16, fontSize: 13 }}>{t('instellingen.logoGeenLogo')}</p>
        )}

        <label className="form-label">{t('instellingen.logoUpload')}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={handleLogoUpload}
            disabled={uploading}
            style={{ fontSize: 13 }}
          />
          {uploading && <span className="text-muted">{t('instellingen.logoUploaden')}</span>}
        </div>
      </div>

      </div>}

      {isAdmin && view === 'locations' && <>
      {/* Locatiebeheer */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{t('instellingen.locatiebeheer')}</h2>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowInactive(!showInactive)}
            >
              {showInactive ? t('admin.hideInactive') : t('admin.showInactive')}
            </button>
            <button className="btn btn-primary" onClick={openNewLocation}>
              + {t('admin.addLocation')}
            </button>
          </div>
        </div>

        {locationsLoading ? (
          <div className="loading-center" style={{ padding: 32 }}><div className="spinner" /></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(showInactive ? locations : locations.filter(l => l.is_active)).length === 0 ? (
              <p className="text-muted" style={{ textAlign: 'center', padding: 32 }}>{t('admin.noLocations')}</p>
            ) : (
              (showInactive ? locations : locations.filter(l => l.is_active)).map(loc => (
                <div
                  key={loc.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 12,
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    opacity: loc.is_active ? 1 : 0.6,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <strong style={{ fontSize: 14 }}>{loc.name}</strong>
                      {!loc.is_active && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#ff5757',
                            background: '#ff575722',
                            padding: '2px 6px',
                            borderRadius: 4,
                          }}
                        >
                          {t('admin.inactive')}
                        </span>
                      )}
                    </div>
                    {loc.address && (
                      <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>{loc.address}</p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-sm"
                      onClick={() => openEditLocation(loc)}
                      style={{ padding: '6px 12px' }}
                    >
                      {t('instellingen.bewerken')}
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => toggleLocationActive(loc)}
                      style={{ padding: '6px 12px' }}
                    >
                      {loc.is_active ? t('admin.deactivate') : t('admin.activate')}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal: locatie toevoegen/bewerken */}
      {locModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>
              {editingLoc ? t('admin.editLocation') : t('admin.newLocation')}
            </h2>

            <form onSubmit={saveLocation}>
              <div className="form-group">
                <label className="form-label">{t('admin.locationName')} *</label>
                <input
                  className="form-input"
                  value={locForm.name}
                  onChange={e => setLocForm(p => ({ ...p, name: e.target.value }))}
                  placeholder={t('admin.locationNamePlaceholder')}
                  disabled={savingLoc}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('admin.locationStreet')}</label>
                <input
                  className="form-input"
                  value={locForm.street}
                  onChange={e => setLocForm(p => ({ ...p, street: e.target.value }))}
                  placeholder={t('admin.locationStreetPlaceholder')}
                  disabled={savingLoc}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('admin.locationHouseNumber')}</label>
                <input
                  className="form-input"
                  value={locForm.house_number}
                  onChange={e => setLocForm(p => ({ ...p, house_number: e.target.value }))}
                  placeholder={t('admin.locationHouseNumberPlaceholder')}
                  disabled={savingLoc}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('admin.locationPostalCode')}</label>
                <input
                  className="form-input"
                  value={locForm.postal_code}
                  onChange={e => setLocForm(p => ({ ...p, postal_code: e.target.value }))}
                  placeholder={t('admin.locationPostalCodePlaceholder')}
                  disabled={savingLoc}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('admin.locationCity')}</label>
                <input
                  className="form-input"
                  value={locForm.city}
                  onChange={e => setLocForm(p => ({ ...p, city: e.target.value }))}
                  placeholder={t('admin.locationCityPlaceholder')}
                  disabled={savingLoc}
                />
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">{t('admin.locationImageUrl')}</label>
                {locForm.image_url && (
                  <img src={locForm.image_url} alt="" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 12, marginBottom: 10 }} />
                )}
                <label className="btn btn-ghost" style={{ cursor: locationImageUploading ? 'wait' : 'pointer' }}>
                  {locationImageUploading ? t('coachBio.fotoUploaden') : t('coachBio.fotoKiezen')}
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={locationImageUploading || savingLoc} onChange={e => handleLocationImageUpload(e.target.files?.[0])} />
                </label>
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setLocModal(false)}
                  disabled={savingLoc}
                >
                  {t('admin.annuleren')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={savingLoc}>
                  {savingLoc ? '…' : t('admin.opslaan')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      </>}

      {autoRecurringInvoicesConfirm && (
        <ConfirmModal
          title={t('instellingen.autoRecurringInvoicesConfirmTitel')}
          body={t('instellingen.autoRecurringInvoicesConfirmTekst')}
          onConfirm={() => {
            setEmailAuto(p => ({ ...p, email_auto_recurring_invoices: 'true' }));
            setAutoRecurringInvoicesConfirm(false);
          }}
          onClose={() => setAutoRecurringInvoicesConfirm(false)}
          danger
        />
      )}

      {locToggleConfirm && (
        <ConfirmModal
          title={t('admin.locationToggleTitel')}
          body={t('admin.locationToggleConfirm', {
            action: locToggleConfirm.is_active ? t('admin.deactivate') : t('admin.activate'),
            name: locToggleConfirm.name,
          })}
          onConfirm={handleLocationToggleConfirm}
          onClose={() => setLocToggleConfirm(null)}
          danger={locToggleConfirm.is_active}
        />
      )}

      {missingTextsModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('instellingen.ontbrekendeTekstenTitel')}</h2>
              <button className="modal-close" onClick={() => setMissingTextsModal(null)}>✕</button>
            </div>
            <p style={{ marginBottom: 24, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
              {t('instellingen.ontbrekendeTekstenBody')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setMissingTextsModal(null)}>
                {t('facturen.annuleren')}
              </button>
              <Link
                to={`/beheer/communicatie/email#${missingTextsModal}_reminder`}
                className="btn btn-primary"
                onClick={() => setMissingTextsModal(null)}
              >
                {t('instellingen.naarTekstenPagina')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
